'use client'
import classNames from 'classnames'
import { type FC, useState } from 'react'
import styles from './HelpCenterPanel.module.scss'
import { useLocalisation } from '@/tools/i18n/client'
import { useErrorHandling } from '@/tools/o11y/client'
import { ChevronDownIcon, IconSize, LoadingIndicator } from '@/tools/ui'

interface Props {
  children: React.ReactNode
  title: string
  isLoading: boolean
  hasLoadFailed: boolean
  onClose: () => void
}

export const HelpCenterPanel: FC<Props> = ({
  children,
  title,
  isLoading,
  hasLoadFailed,
  onClose,
}) => {
  const { t } = useLocalisation()
  const { withErrorTracking } = useErrorHandling()

  const shouldShowError = !isLoading && hasLoadFailed
  const shouldShowIframe = !isLoading && !hasLoadFailed

  const [isClosing, setIsClosing] = useState(false)

  const handleClose = withErrorTracking(() => {
    if (!isClosing) {
      setIsClosing(true)
      setTimeout(() => onClose(), 300)
    }
  })

  return (
    <div className={classNames(styles.container, isClosing ? styles.slideOut : styles.slideIn)}>
      <div className={styles.header}>
        <h3 className={classNames(styles.title, 'pintxo-typography-title2')}>{title}</h3>
        <ChevronDownIcon
          className={styles.closeIcon}
          data-testid="close-icon"
          size={IconSize.large}
          onClick={handleClose}
        />
      </div>
      <div className={styles.content}>
        {isLoading && <LoadingIndicator data-testid="loading-indicator" />}
        {shouldShowError && <p className={styles.error}>{t('generic.error.wentwrong')}</p>}
        {shouldShowIframe && <div className={styles.iframeWrapper}>{children}</div>}
      </div>
    </div>
  )
}
